import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import AppLayout from '../../components/common/AppLayout';
import Spinner from '../../components/common/Spinner';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import adminApi from '../../services/admin.api';

function AdminDataEditorPage() {
  // State
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [schema, setSchema] = useState(null);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [recordHistory, setRecordHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');

  // Load tables on mount
  useEffect(() => {
    loadTables();
  }, []);

  // Load records when table changes
  useEffect(() => {
    if (selectedTable) {
      loadSchema();
      loadRecords();
    }
  }, [selectedTable, pagination.page, search, sortBy, sortOrder]);

  const loadTables = async () => {
    try {
      const response = await adminApi.listTables();
      setTables(response.data.tables);
    } catch (error) {
      toast.error('Failed to load tables');
    }
  };

  const loadSchema = async () => {
    try {
      const response = await adminApi.getTableSchema(selectedTable);
      setSchema(response.data.schema);
    } catch (error) {
      toast.error('Failed to load table schema');
    }
  };

  const loadRecords = useCallback(async () => {
    if (!selectedTable) return;
    setLoading(true);
    try {
      const response = await adminApi.listRecords(selectedTable, {
        page: pagination.page,
        limit: pagination.limit,
        search,
        sortBy,
        sortOrder
      });
      setRecords(response.data.records);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [selectedTable, pagination.page, pagination.limit, search, sortBy, sortOrder]);

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    setSchema(null);
    setRecords([]);
    setPagination({ page: 1, limit: 25, total: 0, pages: 0 });
    setSearch('');
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditFormData({ ...record });
    setEditReason('');
    setEditModalOpen(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setEditFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // Only send changed fields
      const updates = {};
      for (const key of Object.keys(editFormData)) {
        if (JSON.stringify(editFormData[key]) !== JSON.stringify(editingRecord[key])) {
          updates[key] = editFormData[key];
        }
      }

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setEditModalOpen(false);
        return;
      }

      await adminApi.updateRecord(selectedTable, editingRecord.id, updates, editReason);
      toast.success('Record updated successfully');
      setEditModalOpen(false);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (record) => {
    setDeletingRecord(record);
    setDeleteReason('');
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await adminApi.deleteRecord(selectedTable, deletingRecord.id, deleteReason);
      toast.success('Record deleted successfully');
      setDeleteModalOpen(false);
      loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete record');
    }
  };

  const handleViewHistory = async (record) => {
    setHistoryLoading(true);
    setHistoryModalOpen(true);
    try {
      const response = await adminApi.getRecordHistory(selectedTable, record.id);
      setRecordHistory(response.data.history);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Render field input based on type
  const renderFieldInput = (field, value, onChange) => {
    const fieldType = field.type;
    const isReadOnly = field.primaryKey || field.fieldName === 'id';

    if (isReadOnly) {
      return (
        <input
          type="text"
          value={value || ''}
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
        />
      );
    }

    if (field.values) {
      // ENUM type
      return (
        <select
          value={value || ''}
          onChange={(e) => onChange(field.fieldName, e.target.value || null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
        >
          <option value="">-- Select --</option>
          {field.values.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      );
    }

    if (fieldType === 'BOOLEAN') {
      return (
        <select
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => onChange(field.fieldName, e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
        >
          <option value="">-- Select --</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (fieldType === 'TEXT') {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(field.fieldName, e.target.value || null)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
        />
      );
    }

    if (fieldType === 'DATE' || fieldType === 'DATEONLY') {
      return (
        <input
          type="date"
          value={value ? value.split('T')[0] : ''}
          onChange={(e) => onChange(field.fieldName, e.target.value || null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
        />
      );
    }

    if (fieldType === 'INTEGER' || fieldType === 'DECIMAL') {
      return (
        <input
          type="number"
          step={fieldType === 'DECIMAL' ? '0.01' : '1'}
          value={value ?? ''}
          onChange={(e) => onChange(field.fieldName, e.target.value ? parseFloat(e.target.value) : null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
        />
      );
    }

    if (fieldType === 'JSONB' || fieldType === 'JSON') {
      return (
        <textarea
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || ''}
          onChange={(e) => {
            try {
              const parsed = e.target.value ? JSON.parse(e.target.value) : null;
              onChange(field.fieldName, parsed);
            } catch {
              // Keep as string if invalid JSON
            }
          }}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent font-mono text-sm"
          placeholder="JSON format"
        />
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(field.fieldName, e.target.value || null)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
      />
    );
  };

  // Format cell value for display
  const formatCellValue = (value, fieldName) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value).substring(0, 50) + '...';
    if (fieldName.includes('Date') || fieldName.includes('At')) {
      const date = new Date(value);
      if (!isNaN(date)) return date.toLocaleString();
    }
    const str = String(value);
    return str.length > 50 ? str.substring(0, 50) + '...' : str;
  };

  // Get display columns (exclude large text fields)
  const displayColumns = schema?.filter(f =>
    !['TEXT', 'JSONB', 'JSON'].includes(f.type) &&
    !f.fieldName.includes('password')
  ).slice(0, 8) || [];

  return (
    <AppLayout>
      <div className="p-4 md:p-6 max-w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-display font-bold text-tenq-black">Admin Data Editor</h1>
          <p className="text-gray-600 mt-1">Edit any table in the database. All changes are logged.</p>
        </div>

        {/* Table Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Table</label>
          <select
            value={selectedTable || ''}
            onChange={(e) => handleTableSelect(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
          >
            <option value="">-- Select a table --</option>
            {tables.map(t => (
              <option key={t.tableName} value={t.tableName}>
                {t.displayName} ({t.tableName})
              </option>
            ))}
          </select>
        </div>

        {/* Records Table */}
        {selectedTable && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            {/* Search and Sort */}
            <div className="p-4 border-b border-gray-100 flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4 items-center">
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent w-64"
                />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="createdAt">Created At</option>
                  <option value="updatedAt">Updated At</option>
                  <option value="id">ID</option>
                  {schema?.filter(f => f.type === 'STRING').slice(0, 5).map(f => (
                    <option key={f.fieldName} value={f.fieldName}>{f.fieldName}</option>
                  ))}
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="DESC">Newest First</option>
                  <option value="ASC">Oldest First</option>
                </select>
              </div>
              <div className="text-sm text-gray-500">
                {pagination.total} records
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="p-8 flex justify-center">
                <Spinner size="lg" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {displayColumns.map(col => (
                        <th key={col.fieldName} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {col.fieldName}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {records.map(record => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        {displayColumns.map(col => (
                          <td key={col.fieldName} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {formatCellValue(record[col.fieldName], col.fieldName)}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <button
                            onClick={() => handleEditClick(record)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleViewHistory(record)}
                            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                          >
                            History
                          </button>
                          <button
                            onClick={() => handleDeleteClick(record)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {records.length === 0 && (
                      <tr>
                        <td colSpan={displayColumns.length + 1} className="px-4 py-8 text-center text-gray-500">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="p-4 border-t border-gray-100">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                />
              </div>
            )}
          </div>
        )}

        {/* Edit Modal */}
        <Modal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          title={`Edit Record`}
          size="lg"
        >
          <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1">
            {schema?.map(field => (
              <div key={field.fieldName}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.fieldName}
                  {!field.allowNull && <span className="text-red-500 ml-1">*</span>}
                  <span className="text-xs text-gray-400 ml-2">({field.type})</span>
                </label>
                {renderFieldInput(field, editFormData[field.fieldName], handleFieldChange)}
              </div>
            ))}
            <div className="pt-4 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Edit (Optional)
              </label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                rows={2}
                placeholder="Why are you making this change?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setEditModalOpen(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={saving}
              className="px-4 py-2 bg-tenq-gold text-tenq-black font-semibold rounded-lg hover:bg-tenq-gold/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>

        {/* History Modal */}
        <Modal
          isOpen={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          title="Edit History"
          size="lg"
        >
          {historyLoading ? (
            <div className="p-8 flex justify-center">
              <Spinner />
            </div>
          ) : recordHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No edit history found</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {recordHistory.map(log => (
                <div key={log.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                        log.operation === 'create' ? 'bg-green-100 text-green-800' :
                        log.operation === 'update' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {log.operation.toUpperCase()}
                      </span>
                      <span className="ml-2 text-sm text-gray-600">
                        by {log.editor?.fullName || log.editor?.nickname || 'Unknown'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(log.editedAt).toLocaleString()}
                    </span>
                  </div>
                  {log.reason && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Reason:</span> {log.reason}
                    </p>
                  )}
                  {log.changes && (
                    <div className="mt-2 space-y-1">
                      {log.changes.map((change, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-medium text-gray-700">{change.field}:</span>
                          <span className="text-red-600 line-through ml-2">
                            {JSON.stringify(change.oldValue)}
                          </span>
                          <span className="mx-2">→</span>
                          <span className="text-green-600">
                            {JSON.stringify(change.newValue)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Record"
          message={
            <div>
              <p className="mb-4">Are you sure you want to delete this record? This action cannot be undone.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Deletion (Optional)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={2}
                  placeholder="Why are you deleting this record?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-transparent"
                />
              </div>
            </div>
          }
          confirmText="Delete"
          confirmVariant="danger"
        />
      </div>
    </AppLayout>
  );
}

export default AdminDataEditorPage;
